import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import alertify from 'alertifyjs';
import { FilterDrawerComponent } from '../../../components/filter-drawer/filter-drawer.component';
import {
  FilterDrawerFieldConfig,
  FilterDrawerValue,
} from '../../../components/filter-drawer/filter-drawer.models';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import {
  MdaListResponse,
  MdaOption,
  UsersFilter,
  UsersPagedResponse,
} from '../../../modules/services/utility-service.models';
import { AdminCreationModalComponent } from '../admin-creation-modal/admin-creation-modal.component';
import {
  AdminCreationFormValue,
  CreateAdminRequest,
} from '../admin-creation-modal/admin-createion.models';
import { HttpErrorResponse } from '@angular/common/http';
import { DropdownMenu } from '../../../components/dropdown-menu/dropdown-menu.component';
import { UserData } from './user-landing.modal';
@Component({
  selector: 'app-user-landing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AdminCreationModalComponent,
    FilterDrawerComponent,
    DropdownMenu,
  ],
  templateUrl: './user-landing.component.html',
  styleUrl: './user-landing.component.scss',
})
export class UserLandingComponent implements OnInit {
  openMenuIndex: number | null = null;
  isCreateModalOpen = false;
  isFilterDrawerOpen = false;
  isSubmitting = false;
  isLoadingUsers = false;
  mode: 'CREATE' | 'UPDATE' = 'CREATE';

  readonly pageSize = 10;
  readonly emptyUsersFilter: UsersFilter = {
    name: '',
    email: '',
    asin: '',
    phone_number: '',
    mda: '',
    start_date: '',
    end_date: '',
  };

  columns = [
    'Users',
    'Account Type',
    'Roles',
    'ASIN',
    'MDA',
    'Date Created',
    'Status',
    'Actions',
  ];

  targetUser: UserData;
  users: UserData[] = [];
  totalItems = 0;
  totalPages = 1;
  currentPage = 1;
  hasNextPage = false;
  hasPreviousPage = false;
  mdaOptions: MdaOption[] = [];
  statuses: any[] = [
    {
      label: 'Active',
      value: 'ACTIVE',
    },
    {
      label: 'In active',
      value: 'INACTIVE',
    },
  ];
  userFilters: UsersFilter = { ...this.emptyUsersFilter };
  userFilterFields: FilterDrawerFieldConfig[] = [];

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
    this.buildUserFilterFields();
    this.getMdas();
    this.fetchUsers();
  }

  get activeFilterCount(): number {
    return Object.values(this.userFilters).filter((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return `${value ?? ''}`.trim().length > 0;
    }).length;
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'approved' || normalizedStatus === 'active') {
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    }

    if (normalizedStatus === 'pending') {
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    }

    if (normalizedStatus === 'rejected') {
      return 'bg-red-50 text-red-700 border border-red-200';
    }

    return 'bg-slate-100 text-slate-500 border border-slate-200';
  }

  getAccountTypeClass(acctountType: string): string {
    const normalizedAcctountType = acctountType.toLowerCase();

    // if (normalizedAcctountType.includes('commissioner')) {
    //   return 'bg-blue-100 text-blue-700';
    // }

    if (normalizedAcctountType.includes('user')) {
      return 'bg-indigo-100 text-indigo-700';
    }

    // if (normalizedAcctountType.includes('assessment')) {
    //   return 'bg-sky-100 text-sky-700';
    // }

    return 'bg-blue-100 text-blue-700';
  }

  openCreateModal(): void {
    this.mode = 'CREATE';
    this.targetUser = null;
    this.isCreateModalOpen = true;
  }

  closeCreateModal(): void {
    this.isCreateModalOpen = false;
    this.isSubmitting = false;
  }

  fetchUsers(page = this.currentPage): void {
    this.isLoadingUsers = true;
    const offset = (page - 1) * this.pageSize;

    this.apiService
      .getUsersPaged(this.buildUsersFilterPayload(), offset, this.pageSize)
      .subscribe({
        next: (response: UsersPagedResponse) => {
          this.users = response.data ?? [];
          this.totalItems =
            response.pagination?.totalItems ?? this.users.length;
          this.totalPages = response.pagination?.totalPages ?? 1;
          this.currentPage = response.pagination?.currentPage ?? page;
          this.hasNextPage = response.pagination?.hasNextPage ?? false;
          this.hasPreviousPage = response.pagination?.hasPreviousPage ?? false;
          this.isLoadingUsers = false;
        },
        error: (error) => {
          this.isLoadingUsers = false;
          this.users = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.currentPage = 1;
          this.hasNextPage = false;
          this.hasPreviousPage = false;
          const message =
            typeof error === 'string'
              ? error
              : 'Unable to fetch users. Please try again.';
          alertify.error(message);
        },
      });
  }

  onCreateUser(payload: AdminCreationFormValue): void {
    if (this.isSubmitting || !payload) return;

    const newUser: CreateAdminRequest = {
      first_name: payload.firstName,
      last_name: payload.lastName,
      middle_name: '',
      email: payload.email,
      date_of_birth: '',
      gender: '',
      asin: payload.asin,
      phone_number: payload.phoneNumber,
      state: 0,
      lga: 0,
      area: 0,
      address: '',
      mda: payload.mda,
      role_ids: payload.roles,
    };

    this.isSubmitting = true;

    this.apiService.post('/create-admin', newUser).subscribe({
      next: (response: { message?: string }) => {
        this.isSubmitting = false;
        this.closeCreateModal();
        alertify.success(response.message || 'User created successfully.');
        this.fetchUsers(1);
      },
      error: (error) => {
        this.isSubmitting = false;
        const message =
          typeof error === 'string'
            ? error
            : 'Unable to create user. Please try again.';
        alertify.error(message);
      },
    });

    // if (payload.role.toUpperCase() === "ADMIN") {
    //   this.apiService.post('/create-admin', newUser).subscribe({
    //     next: (response: { message?: string }) => {
    //       this.isSubmitting = false;
    //       this.closeCreateModal();
    //       alertify.success(response.message || 'User created successfully.');
    //       this.fetchUsers(1);
    //     },
    //     error: (error) => {
    //       this.isSubmitting = false;
    //       const message =
    //         typeof error === 'string'
    //           ? error
    //           : 'Unable to create user. Please try again.';
    //       alertify.error(message);
    //     },
    //   });
    // } else {
    //   this.apiService.post('/create-user', newUser).subscribe({
    //     next: (response: { message?: string }) => {
    //       this.isSubmitting = false;
    //       this.closeCreateModal();
    //       alertify.success(response.message || 'User created successfully.');
    //       this.fetchUsers(1);
    //     },
    //     error: (error) => {
    //       this.isSubmitting = false;
    //       const message =
    //         typeof error === 'string'
    //           ? error
    //           : 'Unable to create user. Please try again.';
    //       alertify.error(message);
    //     },
    //   });
    // }
  }

  onUpdateUser(payload: AdminCreationFormValue): void {
    if (this.isSubmitting || !payload) return;

    console.log(payload);

    const updatedUser: Partial<CreateAdminRequest> = {
      first_name: payload.firstName,
      last_name: payload.lastName,
      email: payload.email,
      asin: payload.asin,
      phone_number: payload.phoneNumber,
      mda: payload.mda,
      role_ids: payload.roles,
    };

    this.isSubmitting = true;
    this.apiService
      .patch(`/update-user/${this.targetUser.id}`, updatedUser)
      .subscribe({
        next: (response: { message?: string }) => {
          this.isSubmitting = false;
          this.closeCreateModal();
          alertify.success(response.message || 'User created successfully.');
          this.fetchUsers(1);
        },
        error: (error) => {
          this.isSubmitting = false;
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message);
          } else {
            alertify.error(
              JSON.stringify(error) ||
                'Unable to create user. Please try again.',
            );
          }
        },
      });
  }

  onDeleteUser(userId: string): void {
    if (this.isSubmitting || !userId) return;

    this.isSubmitting = true;
    this.apiService.delete(`/delete-user/${userId}`).subscribe({
      next: (response: { message?: string }) => {
        this.isSubmitting = false;
        alertify.success(response.message || 'User deleted successfully.');
        this.fetchUsers(1);
      },
      error: (error) => {
        this.isSubmitting = false;
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(
            JSON.stringify(error) || 'Unable to create user. Please try again.',
          );
        }
      },
    });
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  onEdit(user: UserData): void {
    this.openMenuIndex = null;
    this.isCreateModalOpen = true;
    this.mode = 'UPDATE';
    this.targetUser = user;
    console.log('Edit user:', user.id);
  }

  onToggleStatus(user: UserData): void {
    this.openMenuIndex = null;
    this.apiService
      .patch(`/activate-user/${user.id}`, {
        account_status:
          user.account_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
      })
      .subscribe({
        next: (response: {
          data: UserData;
          message: string;
          meta: string;
          pagination: string;
          status: string;
        }) => {
          if (response.status !== 'success') {
            alertify.error(
              response.message ||
                "Something went wrong while trying to toggle the user's atatus",
            );
            return;
          }
          alertify.success(
            response.message || "Successfully updated the user's status",
          );
          this.fetchUsers(1);
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message);
          } else {
            alertify.error(JSON.stringify(error));
          }
        },
      });
    console.log('Toggle user status:', user.id);
  }

  goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoadingUsers) {
      return;
    }

    this.fetchUsers(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (!this.hasNextPage || this.isLoadingUsers) {
      return;
    }

    this.fetchUsers(this.currentPage + 1);
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  applyFilters(rawFilters: FilterDrawerValue): void {
    this.userFilters = {
      ...this.emptyUsersFilter,
      ...Object.fromEntries(
        Object.entries(rawFilters).map(([key, value]) => [
          key,
          `${value ?? ''}`,
        ]),
      ),
    };

    this.isFilterDrawerOpen = false;
    this.fetchUsers(1);
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildUserFilterFields();
      },
      error: () => {
        this.mdaOptions = [];
        this.buildUserFilterFields();
      },
    });
  }

  private buildUsersFilterPayload(): Partial<UsersFilter> {
    return Object.fromEntries(
      Object.entries(this.userFilters).filter(
        ([, value]) => value.trim().length > 0,
      ),
    ) as Partial<UsersFilter>;
  }

  private buildUserFilterFields(): void {
    this.userFilterFields = [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        placeholder: 'Filter by full name',
      },
      {
        key: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'Filter by email address',
      },
      {
        key: 'asin',
        label: 'ASIN',
        type: 'text',
        placeholder: 'Filter by ASIN',
      },
      {
        key: 'phone_number',
        label: 'Phone Number',
        type: 'tel',
        placeholder: 'Filter by phone number',
      },
      {
        key: 'mda',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: this.mdaOptions.map((option) => ({
          label: option.name,
          value: option.id,
          description: option.code,
        })),
      },
      {
        key: 'Status',
        label: 'Status',
        type: 'select',
        placeholder: 'Select Status',
        options: this.statuses,
      },
      {
        key: 'start_date',
        label: 'Start Date',
        type: 'date',
      },
      {
        key: 'end_date',
        label: 'End Date',
        type: 'date',
      },
    ];
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openMenuIndex = null;
    this.closeCreateModal();
    this.closeFilters();
  }
}
